import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { tournamentConverter } from './converters';
import type {
  Tournament,
  TournamentFormat,
  MatchFormat,
  ScoreMode,
  Discipline,
  GroupConfig,
} from './types';

export interface NewTournamentInput {
  name: string;
  date: Date;
  ownerUid: string;
  discipline: Discipline;
  format: TournamentFormat;
  matchFormat: MatchFormat;
  scoreMode: ScoreMode;
  groupConfig?: GroupConfig;
}

const TRANSLIT: Record<string, string> = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z',
  и: 'i', й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r',
  с: 's', т: 't', у: 'u', ф: 'f', х: 'h', ц: 'ts', ч: 'ch', ш: 'sh',
  щ: 'sch', ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
};

function makeSlug(name: string, ownerUid: string): string {
  const base = name
    .toLowerCase()
    .split('')
    .map((ch) => TRANSLIT[ch] ?? ch)
    .join('')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
  const suffix = ownerUid.slice(0, 6);
  return `${base || 'tournament'}-${suffix}-${Date.now().toString(36)}`;
}

export const tournamentsCol = () => collection(db, 'tournaments').withConverter(tournamentConverter);
export const tournamentDoc = (id: string) => doc(db, 'tournaments', id).withConverter(tournamentConverter);

export async function createTournament(input: NewTournamentInput): Promise<string> {
  const ref = doc(collection(db, 'tournaments'));
  const tournament: Omit<Tournament, 'createdAt' | 'updatedAt'> & {
    createdAt: ReturnType<typeof serverTimestamp>;
    updatedAt: ReturnType<typeof serverTimestamp>;
  } = {
    id: ref.id,
    slug: makeSlug(input.name, input.ownerUid),
    name: input.name,
    date: Timestamp.fromDate(input.date),
    ownerUid: input.ownerUid,
    discipline: input.discipline,
    format: input.format,
    matchFormat: input.matchFormat,
    scoreMode: input.scoreMode,
    status: 'DRAFT',
    seedingMode: 'RANDOM',
    participantCount: 0,
    ...(input.groupConfig ? { groupConfig: input.groupConfig } : {}),
    grandFinalReset: false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  await setDoc(ref, tournament);
  return ref.id;
}

export async function deleteTournament(id: string): Promise<void> {
  await deleteDoc(doc(db, 'tournaments', id));
}
